import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ICliente } from 'modelos/src';
import { ClienteSelectorCacheService } from '../../../../../../componentes/selectores/cliente-selector/cliente-selector-cache.service';
import {
  clientModules,
  clienteTieneModulos,
} from '../../../../../../componentes/selectores/cliente-selector/cliente-modulos.util';
import { LoginService } from '../../../../../login/login.service';
import { MapInstanceFilter } from '../../interfaces';

/**
 * Resuelve la jerarquía de clientes para el filtro único del mapa de vehículos.
 *
 * El filtro guarda la selección tal cual la hizo el usuario (`idsClientes`) más
 * el flag `includeChildren`. Los consumidores necesitan la lista ya expandida
 * (selección + descendientes), y de eso se encarga `expandir()`.
 *
 * `idsAncestros` de un cliente contiene sus ancestros (no a sí mismo), así que
 * un descendiente a cualquier profundidad tiene el id seleccionado ahí dentro.
 */
@Injectable({ providedIn: 'root' })
export class ClientesJerarquiaService {
  private cacheClientes = inject(ClienteSelectorCacheService);

  private clientes: ICliente[] = [];
  private carga?: Promise<ICliente[]>;

  /** Cliente del permiso activo (el que se usa como filtro inicial). */
  public static idClienteActual(): string | undefined {
    return (
      LoginService.getCliente()?._id ?? LoginService.getClientePropio()?._id
    );
  }

  /** Módulos con los que se acota la jerarquía en el mapa de vehículos. */
  private static readonly MODULOS_MAPA: clientModules[] = ['moduloVehiculos'];

  /**
   * Filtro con el que arranca el mapa: el cliente actual del permiso Y su
   * jerarquía, aplicado por igual a vehículos, ubicaciones y recorridos.
   *
   * Los ids van ya expandidos porque el mapa filtra cada reporte contra esta
   * lista (`shouldShowReport`), no vuelve a resolver la jerarquía. Por eso hace
   * falta el árbol cargado: sin él sólo quedaría el cliente propio.
   */
  public async filtroPorDefecto(): Promise<MapInstanceFilter> {
    const idCliente = ClientesJerarquiaService.idClienteActual();
    const seleccion = idCliente ? [idCliente] : [];

    await this.cargar();
    const ids = this.expandir(
      seleccion,
      true,
      ClientesJerarquiaService.MODULOS_MAPA,
    );

    return {
      idsClientes: [...seleccion],
      includeChildren: true,
      idsClientesVehiculos: [...ids],
      idsClientesUbicaciones: [...ids],
      idsClientesRecorridos: [...ids],
    };
  }

  /** Carga (una sola vez) el árbol de clientes visible para el permiso. */
  public async cargar(): Promise<ICliente[]> {
    if (this.clientes.length) return this.clientes;
    if (!this.carga) {
      this.carga = firstValueFrom(this.cacheClientes.getClientes()).then(
        (clientes) => {
          this.clientes = clientes || [];
          this.carga = undefined;
          return this.clientes;
        },
      );
    }
    return this.carga;
  }

  /**
   * Devuelve la selección expandida con los clientes hijos cuando
   * `includeChildren` es true. Sin árbol cargado devuelve la selección tal cual.
   *
   * `modulos` acota los descendientes a los que tienen esos módulos activos
   * (el mismo criterio con el que el `cliente-selector` arma sus opciones), así
   * la jerarquía no arrastra clientes que no operan el módulo.
   */
  public expandir(
    idsClientes: string[],
    includeChildren: boolean,
    modulos: clientModules[] = [],
  ): string[] {
    const seleccionados = (idsClientes || []).filter((id) => !!id);
    if (!seleccionados.length || !includeChildren) return [...seleccionados];

    const base = new Set(seleccionados);
    const resultado = new Set(seleccionados);

    for (const cliente of this.clientes) {
      if (!cliente._id) continue;
      if (!cliente.idsAncestros?.some((id) => base.has(id))) continue;
      if (!clienteTieneModulos(cliente, modulos)) continue;
      resultado.add(cliente._id);
    }

    return [...resultado];
  }
}
